<script setup>
// Remember to put SETUP in the script tag
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from "vue";
import { useUserStore } from "@/stores/user";
import {
  getUserFlashcards, // Fetches User Flashcards from the database for this session
  createUserFlashcards, // Creates User Flashcards if none are found
  updateUserFlashcard, // Updates FSRS Data
  updateCardActions, // Updates Card Actions table to enable user statistic calculations
  countTodaysCards, // Counts the number of cards reviewed today
  getUserDailyGoal, // Retrieves the user's daily goal
  getTotalAnsweredCards,
  getTimingBasedFsrsRating, // Timing-based FSRS rating (Hard/Good/Easy)
} from "@/Scripts/dbFunctions";
import { supabase } from "@/lib/supabase";
import { updateReviewedCard } from "@/Scripts/FSRS.js";
import { useRouter } from "vue-router";
import { splitSentence } from "@/lib/sentenceUtils";
import { useAudioPreloader } from "@/composables/useAudioPreloader";

const FUNCTIONS_BASE = process.env.VUE_APP_FUNCTIONS_BASE_URL;
const fnUrl = (path) => {
  const base = String(FUNCTIONS_BASE || "").replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "");
  return `${base}/${p}`;
};

const router = useRouter();
const audioPreloader = useAudioPreloader({
  maxEntries: 60,
  concurrency: 3,
  onDebug: (...args) => console.debug(...args),
});
let userLanguageStatsModulePromise = null;
const loadUserLanguageStatsModule = () => {
  if (!userLanguageStatsModulePromise) {
    userLanguageStatsModulePromise = import("@/Scripts/userLanguageStats");
  }
  return userLanguageStatsModulePromise;
};

// iOS PWA detection + audio unlock utilities
const isIOS =
  typeof navigator !== "undefined" &&
  /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
const isStandalone =
  typeof window !== "undefined" &&
  (!!(window.navigator && window.navigator.standalone === true) ||
    (!!window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches));
const isIosPwa = !!(isIOS && isStandalone);
const progressControlsEl = ref(null);
let controlsResizeObserver = null;
let learnViewportBaseline = null;

const updateControlsHeight = () => {
  if (typeof document === "undefined") return;
  const height = progressControlsEl.value?.getBoundingClientRect?.().height;
  if (!height) return;
  document.documentElement.style.setProperty(
    "--learn-controls-height",
    `${Math.ceil(height)}px`
  );
};

// Keep the card geometry stable when the software keyboard opens. The action
// tray may be covered by the keyboard, so hide it instead of moving or
// compressing the card.
const updateKeyboardState = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const viewport = window.visualViewport;
  const viewportWidth = Math.round(viewport?.width || window.innerWidth);
  const viewportHeight = Math.round(viewport?.height || window.innerHeight);

  if (
    !learnViewportBaseline ||
    Math.abs(viewportWidth - learnViewportBaseline.width) > 80
  ) {
    learnViewportBaseline = {
      width: viewportWidth,
      height: Math.max(window.innerHeight, viewportHeight),
    };
  } else {
    learnViewportBaseline.height = Math.max(
      learnViewportBaseline.height,
      window.innerHeight,
      viewportHeight
    );
  }

  const visualInset = viewport
    ? Math.max(
        0,
        window.innerHeight - viewport.height - Math.max(0, viewport.offsetTop)
      )
    : 0;
  const resizedInset = Math.max(
    0,
    learnViewportBaseline.height - viewportHeight
  );

  document.documentElement.classList.toggle(
    "learn-keyboard-open",
    Math.max(visualInset, resizedInset) > 80
  );
};

let _iosAudioCtx = null;
function installIosAudioUnlock() {
  if (!isIOS || typeof window === "undefined") return;
  const unlock = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        if (!_iosAudioCtx) _iosAudioCtx = new Ctx();
        if (_iosAudioCtx && _iosAudioCtx.state !== "running") {
          _iosAudioCtx.resume();
        }
        if (_iosAudioCtx) {
          const src = _iosAudioCtx.createBufferSource();
          src.buffer = _iosAudioCtx.createBuffer(1, 1, 22050); // 1 sample silent blip
          src.connect(_iosAudioCtx.destination);
          src.start(0);
        }
      }
    } catch {
      /* noop */
    }
    window.removeEventListener("touchend", unlock, true);
    window.removeEventListener("click", unlock, true);
    window.removeEventListener("keydown", unlock, true);
  };
  window.addEventListener("touchend", unlock, true);
  window.addEventListener("click", unlock, true);
  window.addEventListener("keydown", unlock, true);
}

// Silence console.log noise for LearnView; rating logs use console.info
if (
  typeof console !== "undefined" &&
  console &&
  typeof console.log === "function"
) {
  console.log = function () {};
}
// Lock body scroll during Learn view (robust via global class)
onMounted(() => {
  document.documentElement.classList.add("learn-no-scroll");
  document.body.classList.add("learn-no-scroll");
  updateKeyboardState();
  window.visualViewport?.addEventListener("resize", updateKeyboardState, {
    passive: true,
  });
  window.visualViewport?.addEventListener("scroll", updateKeyboardState, {
    passive: true,
  });
  window.addEventListener("resize", updateKeyboardState, { passive: true });
  updateControlsHeight();
  if (typeof ResizeObserver !== "undefined" && progressControlsEl.value) {
    controlsResizeObserver = new ResizeObserver(updateControlsHeight);
    controlsResizeObserver.observe(progressControlsEl.value);
  }
  // Install iOS audio unlock when running as a PWA on iPhone/iPad
  if (isIosPwa) {
    installIosAudioUnlock();
  }
  // This module is needed after the first correct answer. Start downloading it
  // while the user is reading the first card instead of on the submit path.
  loadUserLanguageStatsModule().catch(() => {
    userLanguageStatsModulePromise = null;
  });
});
onUnmounted(() => {
  try {
    audioPreloader.dispose();
  } catch (_e) {
    // ignore
  }
  document.documentElement.classList.remove("learn-no-scroll");
  document.documentElement.classList.remove("learn-keyboard-open");
  document.documentElement.style.removeProperty("--learn-controls-height");
  document.body.classList.remove("learn-no-scroll");
  learnViewportBaseline = null;
  controlsResizeObserver?.disconnect();
  controlsResizeObserver = null;
  window.visualViewport?.removeEventListener("resize", updateKeyboardState);
  window.visualViewport?.removeEventListener("scroll", updateKeyboardState);
  window.removeEventListener("resize", updateKeyboardState);
});
const isLocalHost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "::1");

const goToLocalReview = () => {
  const id = currentCard.value?.id;
  if (!id) return;
  router.push({ path: "/__local/card-review", query: { id: String(id) } });
};

const userStore = useUserStore();
const currentFlashcards = ref([]);
const currentIndex = ref(0);
const cardsDoneToday = ref(0);
const userDailyGoal = ref(100);
const inputValue = ref("");
const hasUsedDeletionThisAttempt = ref(false);
const autoSubmitInFlight = ref(false);
const isComposingInput = ref(false);
const answerStatus = ref(null); // null, 'correct', or 'incorrect'. This is used to track whether or not the user got the answer correct and update the color of the UI accordingly.
const isSynonymAnswer = ref(false); // true when the entered word is a synonym of the target word
const alreadyReviewed = ref(false); // false indicates that the card has not already been reviewed and then answered incorrectly. This is used to track whether or not we should updated the card's FSRS status.
const inputEl = ref(null); // This is the input field element
let clearTimerId = null;
let keydownHandler = null;
const attemptStartedAt = ref(0);
const typingStartedAt = ref(0);
const isAudioPlaying = ref(false); // true while any feedback audio is playing; used to block submissions and disable controls
const allowUiDuringAudio = ref(false); // when true, allow UI interactions while audio continues
const suppressPostAudioAdvance = ref(false); // skip auto-advance after correct if we've already advanced during audio
const suppressPostIncorrectReset = ref(false); // skip auto-reset after incorrect if we've already reset during audio

const resetInputAttemptState = () => {
  hasUsedDeletionThisAttempt.value = false;
};
const clearInputValue = () => {
  inputValue.value = "";
  resetInputAttemptState();
};

// Reference to the Enter/Learn button for keyboard-pressed animation
const enterBtnRef = ref(null);
const triggerEnterPressedEffect = () => {
  const el = enterBtnRef.value;
  if (!el) return;
  el.classList.add("pressed-effect");
  setTimeout(() => {
    el.classList.remove("pressed-effect");
  }, 120);
};
const cleanupAndAdvanceAfterCorrect = async () => {
  clearInputValue();
  answerStatus.value = null;
  alreadyReviewed.value = false;
  typingStartedAt.value = 0;
  // keep allowUiDuringAudio true until audio ends
  nextCard();
  await focusBlankInput();
};
const resetAfterIncorrect = async () => {
  clearInputValue();
  answerStatus.value = null;
  alreadyReviewed.value = true; // Indicates that the card has been reviewed
  typingStartedAt.value = 0;
  // keep allowUiDuringAudio true until audio ends
  await focusBlankInput();
};
const submitFromKeyboard = async () => {
  // Animate only when prior audio is still finishing and the new card has no active answer
  if (isAudioPlaying.value && allowUiDuringAudio.value && !answerStatus.value) {
    triggerEnterPressedEffect();
    return;
  }
  // If audio is playing, allow the UI to continue based on the current answer state
  if (isAudioPlaying.value) {
    // Case 1: Correct answer sentence audio is playing — advance immediately
    if (answerStatus.value === "correct") {
      allowUiDuringAudio.value = true;
      suppressPostAudioAdvance.value = true; // prevent the post-audio auto-advance
      triggerEnterPressedEffect();
      await cleanupAndAdvanceAfterCorrect();
      // Keep suppression until the previous audio ends
      suppressPostAudioAdvance.value = true;
      return;
    }
    // Case 2: Incorrect answer word audio is playing — clear input and allow new attempt
    if (answerStatus.value === "incorrect") {
      allowUiDuringAudio.value = true;
      suppressPostIncorrectReset.value = true; // prevent the post-audio auto-reset
      triggerEnterPressedEffect();
      await resetAfterIncorrect();
      // Keep suppression until the previous audio ends
      suppressPostIncorrectReset.value = true;
      return;
    }
  }

  // Prevent Enter during IME composition
  if (isComposingInput.value) return;
  // If auto-submit is in progress, skip manual submission
  if (autoSubmitInFlight.value) return;

  // Block while a previous answer is being processed (allow 'prefill' to advance)
  if (answerStatus.value && answerStatus.value !== "prefill") return;
  triggerEnterPressedEffect();
  await checkAnswer();
};
const durationRecallMs = ref(null);
const showSentenceTranslation = ref(true);
const showPaywall = computed(() => {
  return (
    !fotStatus.value &&
    totalAnsweredCards.value >= 150 &&
    String(subscriptionStatus.value || "").toLowerCase() !== "active"
  );
});
const totalAnsweredCards = ref(0);
const subscriptionStatus = ref(null);
const fotStatus = ref(false);
const isViewingPrevious = ref(false);

// Feedback / problem reporting state
const showReportModal = ref(false);
const reportText = ref("");
const submittingFeedback = ref(false);
const reportTextareaEl = ref(null);

// Open/close helpers
const openReportModal = async () => {
  reportText.value = "";
  submittingFeedback.value = false;
  showReportModal.value = true;

  // Blur flashcard input, then focus textarea
  try {
    const el = Array.isArray(inputEl.value) ? inputEl.value[0] : inputEl.value;
    if (el && typeof el.blur === "function") el.blur();
  } catch (_e) {
    console.debug("openReportModal: blur failed", _e);
  }
  await nextTick();
  try {
    if (
      reportTextareaEl.value &&
      typeof reportTextareaEl.value.focus === "function"
    ) {
      reportTextareaEl.value.focus();
    }
  } catch (_e) {
    console.debug("openReportModal: textarea focus failed", _e);
  }
};
const closeReportModal = () => {
  showReportModal.value = false;
  // Return focus to flashcard input
  focusBlankInput();
};

// Ensure focus stays on the modal while it's open
watch(showReportModal, async (open) => {
  if (open) {
    try {
      const el = Array.isArray(inputEl.value)
        ? inputEl.value[0]
        : inputEl.value;
      if (el && typeof el.blur === "function") el.blur();
    } catch (_e) {
      console.debug("reportModal watcher: blur failed", _e);
    }
    await nextTick();
    try {
      if (
        reportTextareaEl.value &&
        typeof reportTextareaEl.value.focus === "function"
      ) {
        reportTextareaEl.value.focus();
      }
    } catch (_e) {
      console.debug("reportModal watcher: textarea focus failed", _e);
    }
  }
});

// Submit feedback to edge function
const submitFeedback = async () => {
  if (!reportText.value.trim() || submittingFeedback.value) return;
  submittingFeedback.value = true;
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const jwt = session?.access_token;

    const payload = {
      comments: reportText.value,
      severity: "bug",
      email: userStore?.user?.email || undefined,
      context: {
        page: "LearnView",
        cardId: currentCard.value?.id ?? null,
        activeLanguage: activeLanguage.value ?? null,
      },
      metadata: {
        appUrl: typeof window !== "undefined" ? window.location?.href : null,
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : null,
      },
    };

    const res = await fetch(fnUrl("error-report"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(`HTTP ${res.status}: ${txt}`);
    }

    // Close on success
    showReportModal.value = false;
  } catch (err) {
    console.error("Failed to submit feedback:", err);
    alert("Sorry, we couldn't send your feedback. Please try again.");
  } finally {
    submittingFeedback.value = false;
  }
};

// Dynamically size the input to the displayed text while the correct answer is shown.
// We measure the rendered text width (using the input's font) and set an explicit pixel width.
const DEFAULT_INPUT_PX = 56; // default width when empty
const MIN_INPUT_PX = 40; // minimum width for readability
const EXTRA_PX = 14; // small padding so text isn't flush to the edge

// Keep input width in sync on viewport resize (mobile + desktop)
const onResize = () => {
  // Defer to next frame so layout/font metrics are stable
  requestAnimationFrame(() => applyDynamicWidth());
};
if (typeof window !== "undefined") {
  window.addEventListener("resize", onResize, { passive: true });

  // Keyboard navigation: Left Arrow to go back to the previous card (prefilled),
  // Right Arrow to return to the current card. Only allow going back 1 card.
  if (!keydownHandler) {
    keydownHandler = async (e) => {
      if (showReportModal.value) return;
      if (e.key === "ArrowLeft") {
        await goBackOne();
        if (isViewingPrevious.value) e.preventDefault();
      } else if (e.key === "ArrowRight") {
        if (isViewingPrevious.value) {
          await returnToCurrent();
          e.preventDefault();
        }
      }
    };
    window.addEventListener("keydown", keydownHandler, { passive: false });
  }
}

function measureInputPx(text) {
  try {
    const el = Array.isArray(inputEl.value) ? inputEl.value[0] : inputEl.value;
    // Fallback to body font if input not yet mounted
    const font = el
      ? getComputedStyle(el).font
      : getComputedStyle(document.body).font;
    measureInputPx._canvas =
      measureInputPx._canvas || document.createElement("canvas");
    const ctx = measureInputPx._canvas.getContext("2d");
    if (!ctx) {
      // Fallback estimate ~8px per char
      return Math.max(
        MIN_INPUT_PX,
        Math.ceil((text || "").length * 8 + EXTRA_PX)
      );
    }
    ctx.font = font || "16px system-ui";
    const metrics = ctx.measureText(String(text || ""));
    return Math.max(MIN_INPUT_PX, Math.ceil(metrics.width + EXTRA_PX));
  } catch (_e) {
    return Math.max(
      MIN_INPUT_PX,
      Math.ceil((text || "").length * 8 + EXTRA_PX)
    );
  }
}

function applyDynamicWidth(textOverride) {
  const el = Array.isArray(inputEl.value) ? inputEl.value[0] : inputEl.value;
  if (!el) return;

  // Use live override when composing; otherwise fall back to reactive model
  const text =
    textOverride !== undefined
      ? String(textOverride)
      : String(inputValue.value || "");
  const px = text ? measureInputPx(text) : DEFAULT_INPUT_PX;
  el.style.width = Math.max(px, MIN_INPUT_PX) + "px";
}

// IME-aware handlers: size using the element's live value even during composition
const onInputRaw = (e) => {
  const v = e?.target?.value ?? "";
  if (!typingStartedAt.value && v.length > 0) {
    // Starting a new typing attempt: reset deletion/backspace guard
    resetInputAttemptState();
    typingStartedAt.value = Date.now();
    if (durationRecallMs.value == null) {
      durationRecallMs.value =
        typingStartedAt.value -
        (attemptStartedAt.value || typingStartedAt.value);
    }
  }
  applyDynamicWidth(stripZeroWidth(v));
};
const onCompositionStart = () => {
  isComposingInput.value = true;
  if (!typingStartedAt.value) {
    // Starting a new typing attempt (IME): reset deletion/backspace guard
    resetInputAttemptState();
    typingStartedAt.value = Date.now();
    if (durationRecallMs.value == null) {
      durationRecallMs.value =
        typingStartedAt.value -
        (attemptStartedAt.value || typingStartedAt.value);
    }
  }
};
const onCompositionUpdate = (e) => {
  const v = e?.target?.value ?? "";
  applyDynamicWidth(stripZeroWidth(v));
};
const onCompositionEnd = (e) => {
  isComposingInput.value = false;
  const v = e?.target?.value ?? "";
  // v-model will sync right after; size immediately and then auto-submit check after sync
  applyDynamicWidth(stripZeroWidth(v));
  // Auto-enter is disabled; users must manually submit via Enter or button.
  // nextTick(() => {
  //   maybeAutoSubmit();
  // });
};

const onBlankKeydown = (e) => {
  if (e.key === "Backspace" || e.key === "Delete") {
    hasUsedDeletionThisAttempt.value = true;
  }
};

// Recompute width whenever the displayed answer is toggled or changes
watch(
  [answerStatus, inputValue, inputEl],
  () => {
    applyDynamicWidth();
  },
  { immediate: true }
);
const activeLanguage = ref(null);
const nativeLanguage = ref(null);

onMounted(async () => {
  const uid = userStore?.user?.id;
  if (!uid) return;
  try {
    const { data, error } = await supabase
      .from("user_preferences")
      .select("active_language,native_language")
      .eq("user_id", uid)
      .maybeSingle();
    if (!error && data) {
      activeLanguage.value = data.active_language || null;
      nativeLanguage.value = data.native_language || null;
    }
  } catch (e) {
    console.error("Failed to load user language preferences:", e);
  }
});

const vFocus = {
  mounted(el) {
    try {
      if (showReportModal.value) return;
      if (document.activeElement !== el) {
        el.focus();
      }
      // ensure width reflects current content on mount
      applyDynamicWidth();
    } catch (_e) {
      // ignore focus errors (element may not be focusable yet)
    }
  },
  updated(el) {
    try {
      if (showReportModal.value) return;
      if (document.activeElement !== el) {
        el.focus();
      }
      // ensure width reflects current content when updated
      applyDynamicWidth();
    } catch (_e) {
      // ignore focus errors (element may be gone or not focusable)
    }
  },
};

const focusBlankInput = async () => {
  if (showReportModal.value) return;
  await nextTick();

  const tryFocus = () => {
    if (showReportModal.value) return true;
    const el = Array.isArray(inputEl.value) ? inputEl.value[0] : inputEl.value;
    if (el && typeof el.focus === "function") {
      // Audio completion and card updates can arrive after retry typing starts.
      // Restore focus only; leave the user's cursor/selection untouched.
      if (document.activeElement !== el) {
        el.focus();
      }
      return true;
    }
    return false;
  };

  if (!tryFocus()) {
    requestAnimationFrame(() => {
      tryFocus();
    });
  }
};

// Add computed properties for progress tracking
const currentCard = computed(() => currentFlashcards.value[currentIndex.value]);
// Skip invalid cards via a watcher to avoid side effects in computed
watch(
  () => [currentIndex.value, currentFlashcards.value],
  () => {
    const len = currentFlashcards.value.length;
    // Clamp idx to bounds
    let idx = Math.min(currentIndex.value, Math.max(0, len - 1));
    while (idx < len) {
      const card = currentFlashcards.value[idx];
      const valid =
        card &&
        card.language_sentence &&
        card.language_word &&
        card.language_sentence_mappings &&
        card.language_word_mappings;
      if (valid) break;
      console.error("Skipping invalid card before display:", {
        id: card?.id,
        has_language_sentence: !!card?.language_sentence,
        has_language_word: !!card?.language_word,
        has_language_sentence_mappings: !!card?.language_sentence_mappings,
        has_language_word_mappings: !!card?.language_word_mappings,
      });
      idx++;
    }
    if (idx !== currentIndex.value && idx < len) {
      currentIndex.value = idx;
    }
  },
  { immediate: true, deep: true }
);

const progressValue = computed(
  () => Math.round(((cardsDoneToday.value + 1) / 100) * 100) // TODO: Update this so that the denominator is based on the user's daily card goal
);

const activeDots = computed(() => {
  const c = currentCard.value || {};
  const dotsByStateNum = (n) => {
    if (n === 0) return 0; // New
    if (n === 1) return 1; // Learn
    if (n === 2) return 3; // Review
    if (n === 3) return 2; // Re-Learn
    return 1; // Unknown
  };

  // 1) Numeric state
  if (typeof c.state === "number" && Number.isFinite(c.state)) {
    return dotsByStateNum(c.state);
  }

  // 2) Named state strings
  if (typeof c.state === "string") {
    const t = c.state.trim().toLowerCase();
    if (t === "new") return 0;
    if (t === "learn" || t === "learning") return 1;
    if (t === "review") return 3;
    if (t === "relearn" || t === "relearning" || t === "re-learn") return 2;
  }

  // 3) Heuristics/fallbacks when state is missing or unexpected
  const reps = Number(c.reps ?? 0);
  const steps = Number(c.learning_steps ?? 0);
  const scheduled = Number(c.scheduled_days ?? 0);
  const lapses = Number(c.lapses ?? 0);

  // Likely New if never reviewed
  if (!Number.isFinite(reps) || reps <= 0) return 0;

  // Likely Learn if in learning steps or explicitly marked learning
  if (steps > 0) return 1;

  // Likely Review with larger intervals or multiple reps
  if (scheduled >= 14 || reps >= 3) return 3;

  // Likely Re-Learn after a lapse
  if (lapses > 0) return 2;

  // Default to Learn if uncertain
  return 1;
});

const morphoInfo = computed(() => {
  const c = currentCard.value || {};
  const pieces = [
    c.part_of_speech || null,
    c.tense || null,
    c.number || null,
    c.person || null,
    // Mandarin-specific extras (exclude pinyin and character forms)
    c.measure_word || null,
  ]
    .map((x) => (typeof x === "string" ? x.trim() : x))
    .filter((x) => x && !(typeof x === "string" && x.toLowerCase() === "n/a"));
  return pieces.length ? pieces.join(", ") : "";
});

const morphoGenderClass = computed(() => {
  const g = (currentCard.value?.gender || "").toLowerCase();
  if (g === "feminine") return "morpho-feminine";
  if (g === "masculine") return "morpho-masculine";
  return "";
});

const isCardGenerating = computed(() => {
  const c = currentCard.value;
  return !(
    c &&
    c.language_sentence &&
    c.language_word &&
    c.language_sentence_mappings &&
    c.language_word_mappings
  );
});

// Auto-focus input when the card/index changes and on first render
watch(
  () => [currentIndex.value, currentCard.value],
  () => {
    resetInputAttemptState();
    attemptStartedAt.value = Date.now();
    typingStartedAt.value = 0;
    durationRecallMs.value = null;

    if (!showReportModal.value) {
      focusBlankInput();
    }
  },
  { immediate: true }
);

// Audio handling
const playSentenceAudio = (url, opts = {}) => {
  /*
   * Plays the audio for a given URL.
   * iOS PWA quirks:
   * - Autoplay may be blocked if not in a user-gesture chain
   * - Some cases never fire 'ended'/'error'; add a watchdog to avoid hangs
   * Non-iPhone web-apps retain current behavior (no watchdog unless passed).
   */
  if (!url) return Promise.resolve();

  // Check if the URL has been preloaded/warmed
  const warmed = audioPreloader.get(url);

  // Default watchdog: only enable by default on iOS PWA to prevent indefinite hangs
  const defaultWatchdog = isIosPwa ? 1500 : 0;
  const watchdogMs =
    typeof opts.watchdogMs === "number" ? opts.watchdogMs : defaultWatchdog;

  return new Promise((resolve) => {
    const audio = new Audio((warmed && warmed.url) || url);
    try {
      audio.preload = "auto";
      // Avoid setting crossOrigin on iOS PWA to reduce odd failures
      if (!isIosPwa) {
        audio.crossOrigin = "anonymous";
      }
    } catch {
      /* ignore attribute errors */
    }

    let finished = false;
    let started = false;
    let watchdogId = null;
    const cleanup = () => {
      if (finished) return;
      finished = true;
      if (watchdogId) {
        clearTimeout(watchdogId);
        watchdogId = null;
      }
      try {
        audio.removeEventListener("playing", onPlaying);
        audio.removeEventListener("ended", onEnded);
        audio.removeEventListener("error", onError);
      } catch {
        /* ignore */
      }
      isAudioPlaying.value = false;
      allowUiDuringAudio.value = false;
      // Restore focus after audio completes/errors/timeout without selecting retry text.
      setTimeout(() => {
        focusBlankInput();
      }, 0);
      resolve();
    };

    const onPlaying = () => {
      started = true;
      if (watchdogId) {
        clearTimeout(watchdogId);
        watchdogId = null;
      }
      // do not resolve here; wait for 'ended' or 'error'
    };
    const onEnded = () => cleanup();
    const onError = (e) => {
      console.error("Audio play failed:", e);
      cleanup();
    };

    audio.addEventListener("playing", onPlaying, { once: true });
    audio.addEventListener("ended", onEnded, { once: true });
    audio.addEventListener("error", onError, { once: true });

    allowUiDuringAudio.value = false;
    isAudioPlaying.value = true;

    if (watchdogMs && watchdogMs > 0) {
      watchdogId = setTimeout(() => {
        if (!started) {
          cleanup();
        }
      }, watchdogMs);
    }

    const p = audio.play();
    if (p && typeof p.catch === "function") {
      p.catch(onError);
    }
  });
};

const stripDiacritics = (s) =>
  (typeof s === "string" ? s : String(s ?? ""))
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const stripZeroWidth = (s) =>
  (typeof s === "string" ? s : String(s ?? "")).replace(
    /[\u200B-\u200D\u2060\uFEFF]/g,
    ""
  );

// Auto-enter helper disabled along with auto-submit behavior.
// const containsHan = (s) => {
//   const str = typeof s === "string" ? s : String(s ?? "");
//   try {
//     return /\p{Script=Han}/u.test(str);
//   } catch {
//     return /[\u3400-\u9FFF\uF900-\uFAFF]/.test(str);
//   }
// };

const stripLeadingPunctuation = (s) => {
  const str = typeof s === "string" ? s : String(s ?? "");
  const openingChars = new Set([
    '"',
    "“",
    "”",
    "‘",
    "’",
    "«",
    "»",
    "¡",
    "¿",
    "(",
    "[",
    "{",
  ]);
  let i = 0;
  while (i < str.length && (str[i] === " " || openingChars.has(str[i]))) {
    i++;
  }
  return str.slice(i);
};

const getCorrectAnswerFirstLetter = (card) => {
  if (!card) return "";
  const parts = splitSentence(card) || [];
  const before = Array.isArray(parts) ? parts[0] : "";
  const isAtStart =
    Array.isArray(parts) &&
    parts.length >= 2 &&
    parts[1] === "__BLANK__" &&
    stripLeadingPunctuation((before || "").trim()) === "";
  const rawAnswer =
    card?.language === "zh"
      ? card?.pinyin || card?.language_word || ""
      : card?.language_word || "";
  const trimmed = stripDiacritics(String(rawAnswer || "")).trim();
  if (!trimmed) return "";
  const letter = trimmed[0];
  return isAtStart ? letter.toUpperCase() : letter;
};

// Auto-enter helper disabled along with auto-submit behavior.
// const doesInputMatchCorrectAnswer = () => {
//   if (!currentCard.value) return false;
//   const raw = stripZeroWidth(String(inputValue.value || ""));
//   const entered = stripDiacritics(raw).toLowerCase().trim();
//   if (!entered) return false;
//   const correct = stripDiacritics(String(currentCard.value.language_word || ""))
//     .toLowerCase()
//     .trim();
//   if (entered === correct) return true;
//   if (currentCard.value?.language === "zh") {
//     // Require Han characters to avoid auto-submitting on pinyin
//     if (!containsHan(raw)) return false;
//     const traditional = stripDiacritics(
//       String(currentCard.value.language_word_traditional || "")
//     )
//       .toLowerCase()
//       .trim();
//     if (traditional && entered === traditional) return true;
//   }
//   return false;
// };

// Auto-enter logic disabled so users must manually submit answers.
// const maybeAutoSubmit = () => {
//   const raw = stripZeroWidth(String(inputValue.value || ""));
//   if (
//     autoSubmitInFlight.value ||
//     hasUsedDeletionThisAttempt.value ||
//     !currentCard.value ||
//     !raw.trim() ||
//     answerStatus.value ||
//     isSynonymAnswer.value ||
//     isViewingPrevious.value ||
//     isComposingInput.value ||
//     isCardGenerating.value
//   ) {
//     return;
//   }
//   // Auto-submit is disabled for this attempt because a deletion/backspace occurred
//   if (!doesInputMatchCorrectAnswer()) return;
//   autoSubmitInFlight.value = true;
//   Promise.resolve(checkAnswer())
//     .catch((err) => {
//       console.error("Auto-submit failed", err);
//     })
//     .finally(() => {
//       autoSubmitInFlight.value = false;
//     });
// };

// watch([inputValue, currentCard], () => {
//   maybeAutoSubmit();
// });

const checkAnswer = async () => {
  if (!currentCard.value) return;
  if (isAudioPlaying.value && !allowUiDuringAudio.value) return;
  // Short-circuit if already marked correct to avoid duplicate processing
  if (answerStatus.value === "correct") return;
  // When audio is playing for the previous card and we already advanced/reset mid-audio,
  // allow submissions even if answerStatus is null by honoring suppression flags.
  if (
    isAudioPlaying.value &&
    !answerStatus.value &&
    !suppressPostAudioAdvance.value &&
    !suppressPostIncorrectReset.value
  )
    return;
  if (isViewingPrevious.value || answerStatus.value === "prefill") {
    // Simply return to the original current card; do not update FSRS
    isViewingPrevious.value = false;
    if (currentIndex.value < currentFlashcards.value.length - 1) {
      currentIndex.value++;
    }
    clearInputValue();
    answerStatus.value = null;
    isSynonymAnswer.value = false;
    await focusBlankInput();
    return;
  }
  // Prevent submitting while the incorrect answer is being displayed (audio playing)
  if (
    answerStatus.value === "incorrect" &&
    !(isAudioPlaying.value && allowUiDuringAudio.value)
  )
    return;

  // Cancel any previous clear timers (e.g., from a prior incorrect attempt)
  if (clearTimerId != null) {
    clearTimeout(clearTimerId);
    clearTimerId = null;
  }

  // Three things needs to be accomplished:
  // 1. Update the UI to reflect whether or not the user got the word correctly
  // 2. Update the card's FSRS status
  // 3. Move onto the next card

  const enteredWordRaw = stripZeroWidth(inputValue.value).trim();
  const correctWordRaw = currentCard.value.language_word || "";
  const correctWordTraditionalRaw =
    currentCard.value?.language === "zh"
      ? currentCard.value.language_word_traditional || ""
      : "";
  const enteredWord = stripDiacritics(enteredWordRaw).toLowerCase();
  const correctWord = stripDiacritics(correctWordRaw).toLowerCase();
  const correctWordTraditional = stripDiacritics(
    correctWordTraditionalRaw
  ).toLowerCase();
  const currentCardId = currentCard.value.id;

  if (
    enteredWord === correctWord ||
    (currentCard.value?.language === "zh" &&
      correctWordTraditional &&
      enteredWord === correctWordTraditional)
  ) {
    // The user got the word correct
    answerStatus.value = "correct";

    // Start playing the sentence audio immediately to preserve user-gesture chain
    const audioDone = isAudioPlaying.value
      ? Promise.resolve()
      : playSentenceAudio(currentCard.value.language_sentence_audio);

    // Highlighting the correct word a nice pastel green for 3 seconds before moving onto the next card
    {
      const parts = splitSentence(currentCard.value) || [];
      const before = Array.isArray(parts) ? parts[0] : "";
      const isAtStart =
        Array.isArray(parts) &&
        parts.length >= 2 &&
        parts[1] === "__BLANK__" &&
        stripLeadingPunctuation((before || "").trim()) === "";
      const original = currentCard.value.language_word || "";
      const fillWord =
        isAtStart && original
          ? original[0].toUpperCase() + original.slice(1)
          : original;
      inputValue.value = fillWord;
      await nextTick();
      applyDynamicWidth();
      await nextTick();
      applyDynamicWidth();
    }

    // Decide FSRS rating based on timing (Hard | Good | Easy). Incorrect handled elsewhere as "Again".
    const isMobile =
      typeof navigator !== "undefined" &&
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent || ""
      );
    // Define timing inputs for rating before use
    const durationTypingMsForRating = typingStartedAt.value
      ? Date.now() - typingStartedAt.value
      : null;
    const correctAnswerForLen =
      currentCard.value?.language === "zh"
        ? currentCard.value?.pinyin || currentCard.value?.language_word || ""
        : currentCard.value?.language_word || "";
    const correctAnswerCharLenForRating = String(correctAnswerForLen).length;
    const ratingInfo = await getTimingBasedFsrsRating({
      user_id: userStore.user.id,
      language_code: activeLanguage.value,
      on_mobile: isMobile,
      correct: true,
      duration_recall_ms: durationRecallMs.value,
      duration_typing_ms: durationTypingMsForRating,
      correct_answer_character_length: correctAnswerCharLenForRating,
    });
    const ratingMap = { Again: 1, Hard: 2, Good: 3, Easy: 4 };
    const ratingName = ratingInfo?.rating || "Good";
    const ratingNum = ratingMap[ratingName] || 3;
    // Updating the card's rating and scheduling information but not uploading the changes to supabase yet
    const updatedCard = updateReviewedCard(currentCard.value, ratingNum);
    updatedCard.id = currentCardId;

    // If the review status is true, update the card's information within the supabase database
    if (!alreadyReviewed.value) {
      // Updating that card's information within the supabase database
      try {
        await updateUserFlashcard(userStore.user.id, updatedCard);

        // Now that the user has reviewed the card, we will update the card_actions table so that user stats can be calculated
        const durationMs = Date.now() - (attemptStartedAt.value || Date.now());
        const durationTypingMs = typingStartedAt.value
          ? Date.now() - typingStartedAt.value
          : null;
        const correctAnswerForLen =
          currentCard.value?.language === "zh"
            ? currentCard.value?.pinyin ||
              currentCard.value?.language_word ||
              ""
            : currentCard.value?.language_word || "";
        const correctAnswerCharLen = String(correctAnswerForLen).length;
        await updateCardActions(
          userStore.user.id,
          updatedCard.id,
          true,
          durationMs,
          durationTypingMs,
          correctAnswerCharLen,
          typeof navigator !== "undefined" &&
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent || ""
            ),
          durationRecallMs.value
        );
        cardsDoneToday.value++;
        // Update per-user language stats (Good) and log rating with a/b update decision
        try {
          const { updateUserLanguageStats } =
            await loadUserLanguageStatsModule();
          const isMobile =
            typeof navigator !== "undefined" &&
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent || ""
            );
          const res = await updateUserLanguageStats({
            user_id: userStore.user.id,
            language_code: activeLanguage.value,
            on_mobile: isMobile,
            finalCorrectWithoutIncorrect: true,
            acceptedAsSynonym: false,
            durationRecallMs: durationRecallMs.value,
            durationTypingMs,
            correctAnswerCharLength: correctAnswerCharLen,
          });
          const used = !!(res && res.updated && !res.outlier);
          console.info(`[Review] Rating=${ratingName}; update_ab=${used}`);
        } catch (e) {
          console.error("Failed to update user_language_stats:", e);
        }
      } catch (error) {
        console.error("Failed to update flashcard:", error);
      }
    } else {
      // Now that the user has reviewed the card, we will update the card_actions table so that user stats can be calculated
      try {
        const durationMs = Date.now() - (attemptStartedAt.value || Date.now());
        const durationTypingMs = typingStartedAt.value
          ? Date.now() - typingStartedAt.value
          : null;
        const correctAnswerForLen =
          currentCard.value?.language === "zh"
            ? currentCard.value?.pinyin ||
              currentCard.value?.language_word ||
              ""
            : currentCard.value?.language_word || "";
        const correctAnswerCharLen = String(correctAnswerForLen).length;
        await updateCardActions(
          userStore.user.id,
          updatedCard.id,
          false,
          durationMs,
          durationTypingMs,
          correctAnswerCharLen,
          typeof navigator !== "undefined" &&
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent || ""
            ),
          durationRecallMs.value
        );
        cardsDoneToday.value++;
      } catch (error) {
        console.error(
          "Failed to update card actions (alreadyReviewed):",
          error
        );
      }
    }

    // Advance after audio; skip cleanup if UI already advanced
    await audioDone;
    if (!suppressPostAudioAdvance.value) {
      await cleanupAndAdvanceAfterCorrect();
    } else {
      // We already advanced mid-audio; reset suppression now that audio ended
      suppressPostAudioAdvance.value = false;
    }
  } else if (
    Array.isArray(currentCard.value?.language_word_synonyms) &&
    currentCard.value.language_word_synonyms
      .map((s) => stripDiacritics(String(s)).toLowerCase().trim())
      .includes(enteredWord) &&
    enteredWord !== correctWord
  ) {
    // Entered a synonym: neutral case, highlight yellow and show message
    isSynonymAnswer.value = true;
    inputValue.value = getCorrectAnswerFirstLetter(currentCard.value);
    await nextTick();
    applyDynamicWidth();
    // Keep input visible, then clear after 1.5s similar to incorrect flow
    setTimeout(() => {
      clearInputValue();
      isSynonymAnswer.value = false;
      applyDynamicWidth();
      typingStartedAt.value = 0;
      focusBlankInput();
    }, 1500);
    return;
  } else {
    // 3) Additional synonym check via same-translation matching
    try {
      const nativeKey = nativeLanguage?.value || "en";
      const currentMapping = (currentCard.value?.language_word_mappings || {})[
        nativeKey
      ];

      if (
        nativeKey &&
        currentMapping &&
        enteredWord !== correctWord &&
        currentCard.value?.language
      ) {
        const { data: candidates, error: lookupErr } = await supabase
          .from("flashcards")
          .select(
            "id, language, language_word, language_word_mappings, language_word_synonyms"
          )
          .eq("language", currentCard.value.language)
          .contains("language_word_mappings", { [nativeKey]: currentMapping });

        const match = Array.isArray(candidates)
          ? candidates.find(
              (c) =>
                stripDiacritics(String(c.language_word || ""))
                  .toLowerCase()
                  .trim() === enteredWord
            )
          : null;

        if (!lookupErr && match) {
          // Treat as synonym: neutral case, highlight yellow and show message
          isSynonymAnswer.value = true;
          inputValue.value = getCorrectAnswerFirstLetter(currentCard.value);
          await nextTick();
          applyDynamicWidth();
          setTimeout(() => {
            clearInputValue();
            isSynonymAnswer.value = false;
            applyDynamicWidth();
            typingStartedAt.value = 0;
            focusBlankInput();
          }, 1500);

          // 4) Persist newly discovered synonym to reduce future querying
          try {
            const existingSyns = Array.isArray(
              currentCard.value?.language_word_synonyms
            )
              ? currentCard.value.language_word_synonyms.slice()
              : [];
            const normalizedExisting = existingSyns
              .map((s) => stripDiacritics(String(s)).toLowerCase().trim())
              .filter(Boolean);
            if (!normalizedExisting.includes(enteredWord)) {
              const updatedSyns = [...existingSyns, enteredWordRaw];
              // Update local cache
              currentCard.value.language_word_synonyms = updatedSyns;
              // Persist to database
              await supabase
                .from("flashcards")
                .update({ language_word_synonyms: updatedSyns })
                .eq("id", currentCard.value.id);
            }
          } catch (persistErr) {
            console.error("Failed to persist new synonym:", persistErr);
          }

          return;
        }
      }
    } catch (dbErr) {
      console.error("Synonym-by-translation check failed:", dbErr);
    }

    // The user got the word incorrect
    answerStatus.value = "incorrect";

    // Start playing the word audio immediately to preserve user-gesture chain
    const audioDoneIncorrect = isAudioPlaying.value
      ? Promise.resolve()
      : playSentenceAudio(currentCard.value.language_word_audio);

    // Replacing the incorrect word with the correct one (for Mandarin, show pinyin instead of simplified),
    // and then highlighting it a nice pastel red for a moment before clearing
    {
      const parts = splitSentence(currentCard.value) || [];
      const before = Array.isArray(parts) ? parts[0] : "";
      const isAtStart =
        Array.isArray(parts) &&
        parts.length >= 2 &&
        parts[1] === "__BLANK__" &&
        stripLeadingPunctuation((before || "").trim()) === "";
      const original =
        currentCard.value?.language === "zh"
          ? currentCard.value?.pinyin || currentCard.value?.language_word || ""
          : currentCard.value?.language_word || "";
      const fillWord =
        isAtStart && original
          ? original[0].toUpperCase() + original.slice(1)
          : original;
      inputValue.value = fillWord;
      await nextTick();
      applyDynamicWidth();
      await nextTick();
      applyDynamicWidth();
    }

    // Updating the card's rating and scheduling information
    const updatedCard = updateReviewedCard(currentCard.value, 1);
    console.info(`[Review] Rating=Again; update_ab=false`);
    updatedCard.id = currentCardId;

    // Updating that card's information within the supabase database if it hasn't been reviewed yet
    if (!alreadyReviewed.value) {
      try {
        await updateUserFlashcard(
          userStore.user.id,
          updatedCard,
          currentCardId
        );
      } catch (error) {
        console.error("Failed to update flashcard:", error);
      }
    } else {
      // console.log("Card has already been reviewed");
    }

    // Clear input after audio; skip reset if UI already cleared for next attempt
    await audioDoneIncorrect;
    if (!suppressPostIncorrectReset.value) {
      await resetAfterIncorrect();
    } else {
      // We already reset mid-audio; reset suppression now that audio ended
      suppressPostIncorrectReset.value = false;
    }
  }
};

// Navigation methods
const goBackOne = async () => {
  if (
    isAudioPlaying.value ||
    isViewingPrevious.value ||
    currentIndex.value <= 0
  )
    return;

  const prevIdx = currentIndex.value - 1;
  const prevCard = currentFlashcards.value[prevIdx];

  isViewingPrevious.value = true;
  currentIndex.value = prevIdx;

  const parts = splitSentence(prevCard) || [];
  const before = Array.isArray(parts) ? parts[0] : "";
  const isAtStart =
    Array.isArray(parts) &&
    parts.length >= 2 &&
    parts[1] === "__BLANK__" &&
    stripLeadingPunctuation((before || "").trim()) === "";
  const original =
    prevCard?.language === "zh"
      ? prevCard?.language_word || ""
      : prevCard?.language_word || "";
  const fillWord =
    isAtStart && original
      ? original[0].toUpperCase() + original.slice(1)
      : original;

  inputValue.value = fillWord;
  answerStatus.value = "prefill"; // show prefilled answer and make input readonly
  isSynonymAnswer.value = false;

  await nextTick();
  applyDynamicWidth();
  await nextTick();
  applyDynamicWidth();
};

const returnToCurrent = async () => {
  if (!isViewingPrevious.value) return;

  isViewingPrevious.value = false;
  if (currentIndex.value < currentFlashcards.value.length - 1) {
    currentIndex.value++;
  }
  clearInputValue();
  answerStatus.value = null;
  isSynonymAnswer.value = false;
  await focusBlankInput();
};

// Navigation methods
const nextCard = () => {
  if (isAudioPlaying.value) {
    if (!allowUiDuringAudio.value) return;
    if (!answerStatus.value && !suppressPostAudioAdvance.value) return;
  }
  // If user is viewing the previous card, "next" should just return to the original current card
  if (isViewingPrevious.value || answerStatus.value === "prefill") {
    isViewingPrevious.value = false;
    if (currentIndex.value < currentFlashcards.value.length - 1) {
      currentIndex.value++;
    }
    clearInputValue();
    answerStatus.value = null;
    isSynonymAnswer.value = false;
    focusBlankInput();
    return;
  }

  if (clearTimerId != null) {
    clearTimeout(clearTimerId);
    clearTimerId = null;
  }
  if (currentIndex.value < currentFlashcards.value.length - 1) {
    currentIndex.value++;
    // Checking if we need to load more flashcards
    if (currentFlashcards.value.length - currentIndex.value < 8) {
      const user_id = userStore.user.id;
      const user_language = activeLanguage.value;
      getSessionFlashcards(user_id, user_language); // Loading in additional flashcards
    }
  }
};

const populateCards = async (cardsToPopulate = currentFlashcards.value) => {
  console.log("=== POPULATE CARDS STARTED ===");
  // console.log("Total cards to check:", currentFlashcards.value.length);
  // console.log("FUNCTIONS_BASE URL:", FUNCTIONS_BASE);

  // Process cards sequentially instead of simultaneously
  for (const [index, card] of cardsToPopulate.entries()) {
    console.log(
      `\n--- Processing card ${index + 1}/${currentFlashcards.value.length} ---`
    );
    console.log("Card ID:", card.id);
    // console.log("Card data:", {
    //   id: card.id,
    //   language_word: card.language_word,
    //   has_language_sentence: !!card.language_sentence,
    //   has_language_sentence_audio: !!card.language_sentence_audio,
    //   has_language_sentence_mappings: !!card.language_sentence_mappings,
    //   has_language_word_audio: !!card.language_word_audio,
    //   has_language_word_mappings: !!card.language_word_mappings,
    // });

    const needsPopulation = (() => {
      const baseMissing =
        !card.language_sentence ||
        !card.language_sentence_audio ||
        !card.language_sentence_mappings ||
        !card.language_word ||
        !card.language_word_audio ||
        !card.language_word_mappings;

      const mandarinMissing =
        card?.language === "zh" &&
        (!card.language_word_traditional || !card.pinyin || !card.measure_word);

      return baseMissing || mandarinMissing;
    })();

    // console.log("Card needs population:", needsPopulation);

    if (needsPopulation) {
      try {
        // console.log("Getting Supabase session...");
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const jwt = session?.access_token;
        // console.log("JWT token available:", !!jwt);

        const fetch_url = `${FUNCTIONS_BASE}/process-new-card`;
        // console.log("Making request to:", fetch_url);

        const requestBody = {
          id: card.id,
          language_sentence: card.language_sentence,
          language_sentence_audio: card.language_sentence_audio,
          language_sentence_mappings: card.language_sentence_mappings,
          language_word: card.language_word,
          language_word_audio: card.language_word_audio,
          language_word_mappings: card.language_word_mappings,
          // Mandarin-specific fields (pass through so the edge function doesn't need to fetch)
          language_word_traditional: card.language_word_traditional,
          pinyin: card.pinyin,
          measure_word: card.measure_word,
          user_language: card.language,
        };

        const res = await fetch(fetch_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
          },
          body: JSON.stringify(requestBody),
        });

        if (!res.ok) {
          let errorDetails;
          try {
            errorDetails = await res.text();
            console.error("Response body:", errorDetails);

            // Try to parse as JSON for more details
            try {
              const errorJson = JSON.parse(errorDetails);
              console.error("Parsed error JSON:", errorJson);
            } catch {
              console.error("Could not parse error response as JSON");
            }
          } catch (textError) {
            console.error("Could not read error response body:", textError);
          }

          console.error(
            `HTTP ${res.status} error for card ${card.id}:`,
            res.statusText,
            errorDetails
          );
          continue; // Skip to next card on error
        }

        let data;
        try {
          data = await res.json();
          console.log("Successfully parsed response JSON:", data);

          // Update the local card in-place using either full DB row or fallback payload
          const updated = data || {};
          const target = currentFlashcards.value.find(
            (candidate) => candidate?.id === card.id
          );

          if (target && updated) {
            // Prefer DB column names if present; otherwise map fallback fields
            if (
              updated.language_sentence != null &&
              updated.language_sentence !== ""
            ) {
              target.language_sentence = updated.language_sentence;
            } else if (updated.generated_language_sentence) {
              target.language_sentence = updated.generated_language_sentence;
            }

            if (updated.language_sentence_mappings) {
              target.language_sentence_mappings =
                updated.language_sentence_mappings;
            } else if (updated.user_language_sentence) {
              target.language_sentence_mappings = {
                ...(target.language_sentence_mappings || {}),
                en: updated.user_language_sentence, // TODO: derive from user language
              };
            }

            // Only overwrite language_word if server returned it explicitly
            if (updated.language_word != null && updated.language_word !== "") {
              target.language_word = updated.language_word;
            }

            if (updated.language_word_mappings) {
              target.language_word_mappings = updated.language_word_mappings;
            } else if (updated.user_language_word) {
              target.language_word_mappings = {
                ...(target.language_word_mappings || {}),
                en: updated.user_language_word, // TODO: derive from user language
              };
            }

            if (updated.language_sentence_audio) {
              target.language_sentence_audio = updated.language_sentence_audio;
            } else if (updated.sentence_audio_url) {
              target.language_sentence_audio = updated.sentence_audio_url;
            }

            if (updated.language_word_audio) {
              target.language_word_audio = updated.language_word_audio;
            } else if (updated.word_audio_url) {
              target.language_word_audio = updated.word_audio_url;
            }

            // Capture synonyms array if provided
            if (Array.isArray(updated.language_word_synonyms)) {
              target.language_word_synonyms = updated.language_word_synonyms;
            }

            // Mandarin-specific fields (both DB columns and fallback keys)
            if (
              Object.prototype.hasOwnProperty.call(
                updated,
                "language_word_traditional"
              )
            ) {
              target.language_word_traditional =
                updated.language_word_traditional;
            } else if (
              Object.prototype.hasOwnProperty.call(
                updated,
                "generated_language_word_traditional"
              )
            ) {
              target.language_word_traditional =
                updated.generated_language_word_traditional;
            }

            if (Object.prototype.hasOwnProperty.call(updated, "pinyin")) {
              target.pinyin = updated.pinyin;
            } else if (
              Object.prototype.hasOwnProperty.call(
                updated,
                "generated_language_word_pinyin"
              )
            ) {
              target.pinyin = updated.generated_language_word_pinyin;
            }

            if (Object.prototype.hasOwnProperty.call(updated, "measure_word")) {
              target.measure_word = updated.measure_word;
            } else if (
              Object.prototype.hasOwnProperty.call(
                updated,
                "generated_language_word_measure"
              )
            ) {
              target.measure_word = updated.generated_language_word_measure;
            }

            // Morphology fields for Spanish/French/etc.
            if (
              Object.prototype.hasOwnProperty.call(updated, "part_of_speech")
            ) {
              target.part_of_speech = updated.part_of_speech;
            } else if (
              Object.prototype.hasOwnProperty.call(
                updated,
                "word_categorization"
              )
            ) {
              target.part_of_speech = updated.word_categorization;
            }

            if (Object.prototype.hasOwnProperty.call(updated, "gender")) {
              target.gender = updated.gender;
            }

            if (Object.prototype.hasOwnProperty.call(updated, "tense")) {
              target.tense = updated.tense;
            } else if (
              Object.prototype.hasOwnProperty.call(updated, "verb_category")
            ) {
              // Edge function normalizes this server-side, but support fallback payloads
              target.tense = updated.verb_category;
            }

            if (Object.prototype.hasOwnProperty.call(updated, "number")) {
              target.number = updated.number;
            } else if (
              Object.prototype.hasOwnProperty.call(
                updated,
                "grammatical_number"
              )
            ) {
              target.number = updated.grammatical_number;
            }

            if (Object.prototype.hasOwnProperty.call(updated, "person")) {
              target.person = updated.person;
            } else if (
              Object.prototype.hasOwnProperty.call(
                updated,
                "grammatical_person"
              )
            ) {
              target.person = updated.grammatical_person;
            }

            // Newly generated audio should be warm as soon as this card is
            // ready; do not wait for the rest of the generation queue.
            audioPreloader.preload([
              target.language_sentence_audio,
              target.language_word_audio,
            ]);
          }
        } catch (jsonError) {
          console.error("Failed to parse response as JSON:", jsonError);
          const textResponse = await res.text();
          console.error("Raw response text:", textResponse);
          throw new Error(
            `Invalid JSON response: ${textResponse.substring(0, 200)}`
          );
        }

        // console.log("Edge function response for card", card.id, ":", data);

        // Add a small delay between requests
        console.log("Waiting 100ms before next request...");
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`=== ERROR processing card ${card.id} ===`);
        console.error("Error type:", typeof error);
        console.error("Error name:", error.name);
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
        console.error("Full error object:", error);

        // Continue with next card even on error
        continue;
      }
    }
  }

  audioPreloader.queueForCards(currentFlashcards.value, {
    index: currentIndex.value,
    ahead: 8,
    behind: 1,
  });
  console.log("=== POPULATE CARDS COMPLETED ===");
};

let populationQueue = Promise.resolve();
const queueCardPopulation = (cards) => {
  populationQueue = populationQueue
    .then(() => populateCards(cards))
    .catch((error) => {
      console.error("Failed to populate flashcard batch:", error);
    });
  return populationQueue;
};

let isLoadingSessionFlashcards = false;
const getSessionFlashcards = async (user_id, user_language) => {
  if (!user_id || !user_language) {
    console.error("getSessionFlashcards called without valid parameters", {
      user_id,
      user_language,
    });
    return;
  }
  if (isLoadingSessionFlashcards) return;
  isLoadingSessionFlashcards = true;

  try {
    // Build a set of existing IDs to prevent duplicates
    const currentIds = new Set(
      Array.isArray(currentFlashcards.value)
        ? currentFlashcards.value.map((card) => card?.id).filter(Boolean)
        : []
    );

    // Initial fetch
    const { data, error } = await getUserFlashcards(
      user_id,
      Array.from(currentIds),
      10, // Limit to 10 cards per session
      user_language
    );
    console.log("Fetched cards data: ", data);

    if (error || !data) {
      console.log("Error fetching flashcards:", error);
      return;
    }

    let cards = data;

    // If none found, create and re-fetch
    if (cards.length === 0) {
      console.log("No flashcards found for user:", user_id);
      await createUserFlashcards(user_language, user_id);

      // Optional tiny delay if needed
      // await new Promise((r) => setTimeout(r, 100));

      const { data: data2, error: error2 } = await getUserFlashcards(
        user_id,
        Array.from(currentIds),
        10,
        user_language
      );

      if (error2 || !data2) {
        console.log("Error re-fetching flashcards:", error2);
        return;
      }

      cards = data2;
    } else {
      console.log("User has flashcards:", cards);
    }

    // Append deduped list
    const deduped = [
      ...currentFlashcards.value,
      ...cards.filter((c) => !currentIds.has(c.id)),
    ];
    currentFlashcards.value = deduped;
    audioPreloader.queueForCards(currentFlashcards.value, {
      index: currentIndex.value,
      ahead: 8,
      behind: 1,
    });

    // Populate any missing fields
    queueCardPopulation(cards);
  } finally {
    isLoadingSessionFlashcards = false;
  }
};

// splitSentence moved to shared utility. Imported from "@/lib/sentenceUtils".

watch(
  () => [userStore.user?.id, activeLanguage.value],
  ([uid, lang]) => {
    const language = lang;
    if (uid && language) {
      getSessionFlashcards(uid, language);
    }
  },
  { immediate: true }
);

//
// Preload audio whenever index or list length changes
//
watch(
  () => [currentIndex.value, currentFlashcards.value.length],
  () => {
    audioPreloader.queueForCards(currentFlashcards.value, {
      index: currentIndex.value,
      ahead: 8,
      behind: 1,
    });
  },
  { immediate: true }
);

onMounted(async () => {
  // Once the screen loads we will get the number of cards done today
  if (userStore.user?.id) {
    try {
      cardsDoneToday.value = await countTodaysCards(userStore.user.id);
      console.log(`Cards done today: ${cardsDoneToday.value}`);
      const { data } = await supabase.auth.getSession();
      console.log(data.session.access_token);
    } catch (e) {
      console.error("Failed to count today's cards:", e?.message || e);
      cardsDoneToday.value = 0;
    }
    try {
      // Getting the users daily goal
      userDailyGoal.value = await getUserDailyGoal(userStore.user.id);
      console.log(`User's daily goal: ${userDailyGoal.value}`);
    } catch (error) {
      console.error(
        "Failed to get user's daily goal:",
        error?.message || error
      );
    }

    // Paywall checks: fetch subscription/fot status and total answered cards
    try {
      const { data: prefs, error: prefError } = await supabase
        .from("user_preferences")
        .select("subscription_status,fot_status")
        .eq("user_id", userStore.user.id)
        .maybeSingle();
      if (!prefError && prefs) {
        subscriptionStatus.value = prefs.subscription_status ?? null;
        fotStatus.value = !!prefs.fot_status;
      }
    } catch (e) {
      console.error(
        "Failed to load subscription preferences:",
        e?.message || e
      );
    }

    try {
      totalAnsweredCards.value = await getTotalAnsweredCards(
        userStore?.user?.id
      );
    } catch (e) {
      console.error("Failed to fetch total answered cards:", e);
      totalAnsweredCards.value = 0;
    }

    // Paywall is now computed reactively; log criteria for visibility
  }
});

onUnmounted(() => {
  if (clearTimerId != null) {
    clearTimeout(clearTimerId);
    clearTimerId = null;
  }
  if (typeof window !== "undefined") {
    window.removeEventListener("resize", onResize);
    if (keydownHandler) {
      window.removeEventListener("keydown", keydownHandler);
      keydownHandler = null;
    }
  }
});
</script>

<template>
  <div class="super">
    <div v-if="showPaywall" class="paywall">
      <div class="paywall-card">
        <h2>Keep learning with Lexical</h2>
        <p>You've answered over 150 cards. Subscribe to continue reviewing.</p>
        <div
          class="paywall-actions"
          style="
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            justify-content: center;
          "
        >
          <router-link class="btn btn-accent btn-lg" to="/subscriptions"
            >See plans</router-link
          >
        </div>
        <div style="text-align: center; margin-top: 6px">
          <a
            href="mailto:taylorboan@me.com"
            style="
              font-size: 0.9rem;
              color: var(--accent);
              text-decoration: none;
            "
          >
            Contact support
          </a>
        </div>
      </div>
    </div>
    <!-- Flashcard Content -->
    <div class="flashcard-container" v-if="!showPaywall">
      <div class="learn-header-row">
        <div class="beta-pill">Beta</div>
      </div>
      <!-- Desktop keeps one bottom tray; mobile separates progress from the controls. -->
      <div class="progress-bar">
        <div class="progress-bar-container">
          <p class="progress-label progress-label-start">
            {{ cardsDoneToday }}
          </p>
          <progress
            id="progress-bar"
            :value="progressValue"
            :max="userDailyGoal"
          ></progress>
          <p class="progress-label progress-label-end">
            {{ userDailyGoal }}
          </p>
        </div>
        <div ref="progressControlsEl" class="progress-controls">
          <button
            type="button"
            class="btn btn-ghost"
            style="border: none"
            @click="isViewingPrevious ? returnToCurrent() : goBackOne()"
            :disabled="
              (!isViewingPrevious && currentIndex === 0) ||
              isCardGenerating ||
              isAudioPlaying
            "
            :aria-label="
              isViewingPrevious ? 'Return to current card' : 'Go back one card'
            "
            :title="
              isViewingPrevious ? 'Return to current card' : 'Go back one card'
            "
          >
            <img
              class="arrow-icon"
              src="../assets/LeftArrow.svg"
              alt=""
              :style="{
                width: '18px',
                height: '18px',
                transform: isViewingPrevious ? 'rotate(180deg)' : 'none',
              }"
            />
          </button>
          <button
            type="button"
            class="btn btn-accent enter-btn"
            ref="enterBtnRef"
            @click="checkAnswer"
            :disabled="
              isCardGenerating ||
              !!answerStatus ||
              (isAudioPlaying && (!allowUiDuringAudio || !answerStatus))
            "
            :aria-label="
              inputValue && inputValue.trim().length > 0
                ? 'Enter word'
                : 'Learn Word'
            "
            :title="
              inputValue && inputValue.trim().length > 0
                ? 'Enter word'
                : 'Learn Word'
            "
          >
            {{
              inputValue && inputValue.trim().length > 0
                ? "Enter word"
                : "Learn Word"
            }}
            <img
              class="keyboard-return-icon desktop-only"
              src="../assets/KeyboardReturnIcon.svg"
              alt=""
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
      <div
        v-if="currentFlashcards.length && currentCard"
        class="flashcard-card"
        style="position: relative"
      >
        <div
          v-if="isCardGenerating"
          class="loading-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div class="spinner"></div>
          <div class="loading-text">Generating your flashcard…</div>
        </div>
        <div
          class="state-dots"
          style="
            position: absolute;
            top: 30px;
            left: var(--card-left-pad, 1.5rem);
            display: flex;
            gap: 6px;
          "
        >
          <span
            :style="{
              width: '18px',
              height: '4px',
              borderRadius: '20%',
              background: activeDots >= 1 ? '#8AD1A9' : '#ddd',
              display: 'inline-block',
            }"
          ></span>
          <span
            :style="{
              width: '18px',
              height: '4px',
              borderRadius: '20%',
              background: activeDots >= 2 ? '#8AD1A9' : '#ddd',
              display: 'inline-block',
            }"
          ></span>
          <span
            :style="{
              width: '18px',
              height: '4px',
              borderRadius: '20%',
              background: activeDots >= 3 ? '#8AD1A9' : '#ddd',
              display: 'inline-block',
            }"
          ></span>
        </div>
        <!-- Sentence with blank -->
        <button
          v-if="isLocalHost"
          class="card-menu-btn"
          style="right: 48px"
          aria-label="Review in Dashboard"
          @click="goToLocalReview"
          title="Open this card in the local review dashboard"
        >
          🔧
        </button>
        <button
          class="card-menu-btn"
          aria-label="More options"
          @click="openReportModal"
        >
          ⋮
        </button>

        <p class="sentence-blank">
          <template v-for="(part, i) in splitSentence(currentCard)" :key="i">
            <input
              ref="inputEl"
              v-focus
              v-if="part === '__BLANK__'"
              type="text"
              class="word-blank"
              autocapitalize="none"
              autocorrect="off"
              spellcheck="false"
              autocomplete="off"
              inputmode="text"
              enterkeyhint="done"
              :readonly="
                !!answerStatus ||
                isCardGenerating ||
                (isAudioPlaying && !allowUiDuringAudio)
              "
              :key="`blank-${currentIndex}-${i}`"
              :class="{
                'correct-answer': answerStatus === 'correct',
                'incorrect-answer': answerStatus === 'incorrect',
                'synonym-answer': isSynonymAnswer,
              }"
              v-model="inputValue"
              @input="onInputRaw"
              @compositionstart="onCompositionStart"
              @compositionupdate="onCompositionUpdate"
              @compositionend="onCompositionEnd"
              @keydown="onBlankKeydown"
              @keyup.enter="submitFromKeyboard"
            />
            <span v-else>{{ part }}</span>
          </template>
        </p>

        <!-- Morphological info -->
        <div
          v-if="morphoInfo"
          class="morpho-box"
          :class="morphoGenderClass"
          aria-label="part of speech and tense"
        >
          <span class="morpho-chip">{{ morphoInfo }}</span>
        </div>

        <!-- Translations -->
        <div
          class="translations"
          style="padding-left: 0; height: auto; justify-content: flex-start"
        >
          <div
            class="translations-inner"
            :style="{ paddingLeft: 'var(--card-left-pad, 1.5rem)' }"
          >
            <p class="word-translation">
              {{ currentCard.language_word_mappings?.en }}
            </p>
            <p class="sentence-translation" v-show="showSentenceTranslation">
              {{ currentCard.language_sentence_mappings?.en }}
            </p>
            <p v-if="isSynonymAnswer" class="synonym-note">
              "{{ inputValue }}" is a synonym.
            </p>
          </div>
          <!-- synonyms hidden -->
          <button
            class="translate-toggle"
            :aria-expanded="showSentenceTranslation"
            aria-label="Toggle sentence translation"
            @click="showSentenceTranslation = !showSentenceTranslation"
          >
            <img
              src="../assets/RightArrow.svg"
              alt=""
              :class="{ rotated: showSentenceTranslation }"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Report modal -->
    <div
      v-if="showReportModal"
      class="report-modal-overlay"
      @click.self="closeReportModal"
    >
      <div
        class="report-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-title"
      >
        <h3 id="report-title">Report a problem</h3>
        <p class="helper">
          Tell us what went wrong. We'll include technical details
          automatically.
        </p>
        <textarea
          ref="reportTextareaEl"
          v-model="reportText"
          placeholder="Describe the issue..."
          :disabled="submittingFeedback"
          @keydown.stop
          @keyup.stop
          @keypress.stop
          @input.stop
          autofocus
        ></textarea>
        <div class="report-actions">
          <button
            type="button"
            class="cancel-btn"
            @click="closeReportModal"
            :disabled="submittingFeedback"
          >
            Cancel
          </button>
          <button
            type="button"
            class="submit-btn"
            @click="submitFeedback"
            :disabled="!reportText.trim() || submittingFeedback"
          >
            {{ submittingFeedback ? "Submitting..." : "Submit Feedback" }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

// This CSS is used for changing the color of the input field based on whether
or not the user got the answer correct.
<style scoped>
/* Add these styles at the end of the component */
input.word-blank {
  font-family: inherit; /* Match surrounding text font */
  font-size: inherit; /* Match surrounding text size */
  border: none;
  border-radius: 5px;
  padding: 2px 5px;
  line-height: 1.2; /* mobile-friendly line height */
  transition: background-color 0.3s ease;
  max-width: 100%;
  display: inline-block;
  box-sizing: content-box; /* so width set via JS doesn’t include padding */
  overflow: hidden; /* ensure the element itself never overflows its container */
}

input.word-blank.correct-answer,
input.word-blank.correct-answer:focus {
  animation: flash-correct 1800ms ease-in-out;
}

input.word-blank.incorrect-answer,
input.word-blank.incorrect-answer:focus {
  animation: flash-incorrect 700ms ease-in-out;
}

@keyframes flash-correct {
  0% {
    background-color: #f1f3f5;
    color: inherit;
  }
  10%,
  70% {
    background-color: #a8e6cf;
    color: #6bbf99; /* slightly darker pastel green for text */
  }
  100% {
    background-color: #f1f3f5;
    color: inherit;
  }
}

input.word-blank.synonym-answer,
input.word-blank.synonym-answer:focus {
  animation: flash-synonym 1200ms ease-in-out;
}

@keyframes flash-synonym {
  0% {
    background-color: #f1f3f5;
  }
  10%,
  70% {
    background-color: #fff3b0;
  }
  100% {
    background-color: #f1f3f5;
  }
}

.synonym-note {
  font-size: 0.9rem;
  color: #8a8a8a;
  margin: 6px 0 0 0;
}

@keyframes flash-incorrect {
  0% {
    background-color: #f1f3f5;
  }
  10%,
  70% {
    background-color: #ffaaa5;
  }
  100% {
    background-color: #f1f3f5;
  }
}

.morpho-box {
  margin-left: var(--card-left-pad, 1.5rem);
  margin-bottom: 30px;
  display: inline-flex;
  background: #f5f5f5;
  border: 0px;
  border-radius: 6px;
  padding-left: 15px;
  padding-right: 15px;
  padding-top: 5px;
  padding-bottom: 5px;
  color: #374151;
  font-size: 0.9rem;
  width: -moz-fit-content;
  width: fit-content;
  height: 30px;
  max-width: 100%;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 200;
  align-items: center;
}

.morpho-box .morpho-chip {
  display: inline-block;
  white-space: nowrap;
}

.morpho-box.morpho-feminine {
  background: var(--morpho-feminine-bg);
  color: var(--text-color);
}

.morpho-box.morpho-masculine {
  background: var(--morpho-masculine-bg);
  color: var(--text-color);
}
</style>

<style scoped src="../Styles/LearnViewStyle.css"></style>

<style scoped>
/* Theming overrides for Learn View to support dark mode via CSS variables */
.super {
  background: var(--app-bg);
}
/* Header row with right-aligned Beta badge aligned to flashcard */
.learn-header-row {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  max-width: 650px;
  margin: 0 auto 8px auto; /* no extra gap */
  box-sizing: border-box;
}

/* Reduce top margin of flashcard to tighten gap under Beta pill */
.flashcard-container .flashcard-card {
  margin-top: 0;
}

/* Reusable Beta pill using the app accent color */
.beta-pill {
  background: var(--accent);
  color: #fff;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 0.75rem;
  line-height: 1.2;
  font-weight: 700;
}

/* Card and text colors */
.flashcard-card {
  --card-left-pad: 1.5rem;
  background: var(--sidebar-bg);
  color: var(--text-color);
}

.sentence-blank,
.word-translation,
.sentence-translation,
.synonyms {
  color: var(--text-color);
}

/* Translations area background */
.translations {
  background: var(--hover-bg);
  position: relative;
}

/* Inner wrapper to align and inherit text color */
.translations-inner {
  color: var(--text-color);
}

.translate-toggle {
  position: absolute;
  right: 12px;
  top: 12px;
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
}

.translate-toggle img {
  width: 18px;
  height: 18px;
  transition: transform 0.2s ease;
}

.translate-toggle img.rotated {
  transform: rotate(90deg);
}

/* Input (blank) default and focus states */
input.word-blank {
  background: var(--hover-bg, #f1f3f5);
  color: var(--text-color);
  border: none; /* ensure no border is shown */
  line-height: 1.2; /* mobile-friendly line height */
  /* width is controlled via JS to auto-resize by content */
}

input.word-blank:focus:not(.correct-answer):not(.incorrect-answer):not(
    .synonym-answer
  ) {
  background: var(--hover-bg, #f1f3f5);
}

/* Morpho box background and text */
.morpho-box {
  background: var(--hover-bg);
  color: var(--text-color);
}

/* Progress bar container and track/value colors */
.progress-bar {
  background: var(--sidebar-bg);
  margin-bottom: 15px;
}

.progress-bar-container {
  display: flex;
  align-items: center;
}

@media (max-width: 560px) {
  #progress-bar {
    flex: 1 1 auto;
    width: auto;
    min-width: 40px;
  }
}

.progress-bar-container p {
  flex: 0 0 auto;
  width: auto;
  margin: 0 8px;
  font-variant-numeric: tabular-nums;
}

@media (max-width: 768px) {
  .progress-bar-container {
    display: flex;
    width: calc(100% - 20px);
    margin-left: auto;
    margin-right: auto;
  }

  .progress-bar-container #progress-bar {
    flex: 1 1 auto;
    width: auto;
    min-width: 40px;
  }

  .progress-bar-container p:first-child {
    margin-left: 0;
    margin-right: 8px;
  }

  .progress-bar-container p:last-child {
    margin-left: 8px;
    margin-right: 0;
  }
}

#progress-bar,
#progress-bar::-webkit-progress-bar {
  background-color: var(--hover-bg);
}

#progress-bar::-webkit-progress-value,
#progress-bar::-moz-progress-bar {
  background-color: var(--accent);
}

/* State dots: override inline hard-coded colors using CSS variables */
.state-dots span[style*="background: #8AD1A9"] {
  background: var(--accent) !important;
}
.state-dots span[style*="background: #ddd"] {
  background: var(--hover-bg) !important;
}
.sentence-blank {
  word-break: break-word;
  overflow-wrap: anywhere; /* allow wrapping around the input blank */
  padding-left: 0 !important;
  margin-left: var(--card-left-pad, 1.5rem);
}

/* Three-dot card menu button */
.card-menu-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  color: var(--text-color);
  font-size: 20px;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 8px;
  cursor: pointer;
}
.card-menu-btn:hover {
  background: var(--hover-bg);
}

/* Report modal styles */
.report-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.report-modal {
  width: 100%;
  max-width: 92vw;
  background: var(--sidebar-bg);
  color: var(--text-color);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  padding: 16px;
  box-sizing: border-box;
  max-height: 90vh;
  overflow: auto;
  margin: 12px;
}
.report-modal h3 {
  margin: 0 0 6px 0;
}
.report-modal p.helper {
  margin: 0 0 12px 0;
  color: #8a8a8a;
}
.report-modal textarea {
  width: 100%;
  min-height: 120px;
  max-height: 50vh;
  resize: vertical;
  border: 1px solid var(--hover-bg);
  border-radius: 8px;
  padding: 10px;
  background: var(--hover-bg);
  color: var(--text-color);
  font: inherit;
  box-sizing: border-box;
}
.report-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.report-actions .cancel-btn {
  background: var(--hover-bg);
  color: var(--text-color);
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
}
.report-actions .submit-btn {
  background: var(--accent);
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
}
.report-actions .submit-btn[disabled],
.report-actions .cancel-btn[disabled] {
  opacity: 0.7;
  cursor: default;
}

/* Paywall */
.paywall {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: color-mix(in oklab, var(--app-bg) 60%, rgba(0, 0, 0, 0.35));
  z-index: 1000;
}

.paywall-card {
  background: var(--sidebar-bg);
  color: var(--text-color);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  padding: clamp(16px, 3vw, 28px);
  width: min(640px, 92vw);
  text-align: center;
}

.paywall-card h2 {
  margin: 0 0 8px 0;
  font-size: clamp(1.25rem, 1rem + 1vw, 1.8rem);
}

.paywall-card p {
  margin: 0 0 14px 0;
  color: color-mix(in oklab, var(--text-color) 80%, transparent);
}

@media (max-width: 560px) {
  .paywall-card {
    padding: 16px;
  }
  .flashcard-card {
    --card-left-pad: 1.5rem;
  }
}

/* Local button styles (match landing page look-and-feel) */
.btn {
  --btn-bg: var(--hover-bg);
  --btn-fg: var(--text-color);
  --btn-br: 10px;
  --btn-pad: 10px 14px;

  background: var(--btn-bg);
  color: var(--btn-fg);
  border: none;
  border-radius: var(--btn-br);
  padding: var(--btn-pad);
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-ghost {
  background: transparent;
  border: 1px solid color-mix(in oklab, var(--text-color) 12%, transparent);
}

.btn-accent {
  --btn-bg: var(--accent);
  --btn-fg: #fff;
}

.btn-lg {
  --btn-pad: 12px 18px;
  border-radius: 12px;
  font-size: 1rem;
}

/* Progress controls tweaks */
.progress-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-right: 8px;
}

@media (max-width: 768px) {
  .progress-controls {
    justify-content: space-between;
    margin-right: 0;
  }
}

.enter-btn {
  font-weight: 400;
  align-self: center;
  margin-left: auto;
  white-space: nowrap;
  transition: transform 0.06s ease, background 0.15s ease, box-shadow 0.15s ease,
    filter 0.15s ease;
}
.enter-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px color-mix(in oklab, var(--btn-bg) 40%, transparent);
  filter: saturate(1.05);
}
.enter-btn:active:not(:disabled) {
  transform: translateY(0);
  background: color-mix(in oklab, var(--btn-bg) 90%, black 10%);
  filter: saturate(0.98);
}
.enter-btn.pressed-effect {
  transform: translateY(0);
  background: color-mix(in oklab, var(--btn-bg) 90%, black 10%);
  filter: saturate(0.98);
}
.enter-btn .keyboard-return-icon {
  width: 18px;
  height: 18px;
  margin-left: 8px;
}
/* hide icon on small screens */
.desktop-only {
  display: none;
}
@media (min-width: 769px) {
  .desktop-only {
    display: inline-block;
  }
}

/* Arrow icon colors are controlled globally in Styles/LearnViewStyle.css */

/* Loading overlay for when a flashcard is still being generated */
.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(1px);
  background: color-mix(in oklab, var(--sidebar-bg) 70%, transparent);
  z-index: 5;
  border-radius: 16px;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--hover-bg);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 8px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: var(--text-color);
  opacity: 0.85;
  font-size: 0.95rem;
}
</style>
<style>
/* Global scroll lock for Learn view to maintain exact viewport height */
.learn-no-scroll {
  overflow-x: hidden !important;
  overflow-y: hidden !important;
  overscroll-behavior: none;
  height: 100lvh !important;
}
.learn-no-scroll #app {
  height: 100lvh !important;
  overflow-x: hidden !important;
  overflow-y: hidden !important;
  overscroll-behavior: none;
}

@media (max-width: 768px) {
  .learn-keyboard-open .progress-controls {
    visibility: hidden;
    pointer-events: none;
  }
}
</style>
